import React, { useState, useEffect } from 'react';
import { Modal, Banner, FormField } from '@/components/AdminPanel/AdminPrimitives';
import { LCBtn, CLS } from '@/components/common/LCPrimitives';
import apiService from '@/services/apiService';
import { UserOption, ARMAssignmentCreate } from '@/types/admin';

interface CreateARMAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateARMAssignmentDialog: React.FC<CreateARMAssignmentDialogProps> = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<{ arm_user_id: number | null; rm_user_id: number | null; notes: string }>({
    arm_user_id: null, rm_user_id: null, notes: '',
  });
  const [armUsers, setArmUsers] = useState<UserOption[]>([]);
  const [rmUsers, setRmUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [valErrors, setValErrors] = useState<{ arm_user_id?: string; rm_user_id?: string }>({});

  useEffect(() => {
    if (open) {
      setFormData({ arm_user_id: null, rm_user_id: null, notes: '' });
      setError(null);
      setValErrors({});
      fetchUsers();
    }
  }, [open]);

  const fetchUsers = async () => {
    setFetchingUsers(true);
    try {
      const [armsData, rmsData] = await Promise.all([apiService.getARMUsers(), apiService.getRMUsers()]);
      setArmUsers(armsData);
      setRmUsers(rmsData);
      if (!armsData.length) setError('No ARM users found. Please create users with user_type="ARM" first.');
      else if (!rmsData.length) setError('No RM users found. Please create users with user_type="RM" first.');
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally { setFetchingUsers(false); }
  };

  const validate = () => {
    const e: { arm_user_id?: string; rm_user_id?: string } = {};
    if (!formData.arm_user_id) e.arm_user_id = 'ARM User is required';
    if (!formData.rm_user_id) e.rm_user_id = 'RM User is required';
    setValErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true); setError(null);
    try {
      const payload: ARMAssignmentCreate = {
        arm_user_id: formData.arm_user_id!,
        rm_user_id: formData.rm_user_id!,
      };
      if (formData.notes.trim()) payload.notes = formData.notes.trim();
      await apiService.createARMAssignment(payload);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to create assignment');
    } finally { setLoading(false); }
  };

  return (
    <Modal
      open={open}
      onClose={() => !loading && onClose()}
      title="Create ARM Assignment"
      size="md"
      footer={
        <>
          <LCBtn variant="outline" size="sm" onClick={() => !loading && onClose()} disabled={loading}>Cancel</LCBtn>
          <LCBtn
            variant="primary" size="sm" onClick={handleSubmit}
            disabled={loading || fetchingUsers || !armUsers.length || !rmUsers.length}
          >
            {loading ? 'Creating…' : 'Create Assignment'}
          </LCBtn>
        </>
      }
    >
      {error && <Banner variant="error" className="mb-4">{error}</Banner>}
      {fetchingUsers ? (
        <div className="flex justify-center py-6 text-[#7A7A76] text-[13px]">Loading users…</div>
      ) : (
        <div className="flex flex-col gap-3.5">
          <FormField label="ARM User" required error={valErrors.arm_user_id} hint="Select Assistant Relationship Manager">
            <select
              className={CLS.input}
              value={formData.arm_user_id ?? ''}
              onChange={e => {
                setFormData(p => ({ ...p, arm_user_id: e.target.value ? Number(e.target.value) : null }));
                if (e.target.value) setValErrors(p => ({ ...p, arm_user_id: undefined }));
              }}
              disabled={loading || !armUsers.length}
            >
              <option value="">— Select ARM User —</option>
              {armUsers.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
            </select>
          </FormField>

          <FormField label="RM User" required error={valErrors.rm_user_id} hint="Select Relationship Manager">
            <select
              className={CLS.input}
              value={formData.rm_user_id ?? ''}
              onChange={e => {
                setFormData(p => ({ ...p, rm_user_id: e.target.value ? Number(e.target.value) : null }));
                if (e.target.value) setValErrors(p => ({ ...p, rm_user_id: undefined }));
              }}
              disabled={loading || !rmUsers.length}
            >
              <option value="">— Select RM User —</option>
              {rmUsers.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
            </select>
          </FormField>

          <FormField label="Notes" hint="Optional notes about this assignment (max 500 characters)">
            <textarea
              className={`${CLS.input} min-h-[80px] resize-y`}
              value={formData.notes}
              onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
              maxLength={500}
              disabled={loading}
            />
          </FormField>
        </div>
      )}
    </Modal>
  );
};

export default CreateARMAssignmentDialog;
